if (!window.HTMLDialogElement) {
  let activeDialog: HTMLDialogElement | null = null;

  const preventBlurListener = (e: FocusEvent): void => {
    if (!e.relatedTarget) return;
    if (!activeDialog?.contains(e.relatedTarget as Node)) {
      setTimeout(() => {
        (e.target as HTMLElement).focus();
      });
    }
  };

  const escListener = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      const event = new Event("cancel", { bubbles: true, cancelable: true });
      activeDialog?.dispatchEvent(event);
      if (!event.defaultPrevented) {
        activeDialog?.close();
      }
    }
  };

  window.HTMLDialogElement = HTMLUnknownElement as any;

  const proto = HTMLUnknownElement.prototype as any;

  let maxZIndex = 1000;

  proto.showModal = function (): void {
    this.__lastActiveElement = document.activeElement as HTMLElement | null;

    this.style.display = "block";
    setTimeout(() => {
      this.style.display = "";
      this.show();
      this.setAttribute("aria-modal", "true");
    });
    this.classList.add("dialog-polyfill-as-modal");

    const focusableEl = this.querySelector("[autofocus]") || this.querySelector("a[href],button,input,textarea,select,details,[contenteditable],[tabindex]");
    (focusableEl as HTMLElement)?.focus();

    this.addEventListener("blur", preventBlurListener, true);
    addEventListener("keydown", escListener, true);

    if (!this.__backdrop) {
      this.__backdrop = document.createElement("div");
      this.__backdrop.classList.add("backdrop");
    }
    this.__backdrop.style.zIndex = (maxZIndex++).toString();
    this.after(this.__backdrop);

    this.style.zIndex = (maxZIndex++).toString();

    activeDialog = this;
  };

  proto.show = function (): void {
    this.setAttribute("open", "");
    this.setAttribute("role", "dialog");
  };

  proto.close = function (returnValue?: string): void {
    // Add closing class for animation
    this.classList.add("closing");

    // Wait for animation to complete before actually closing
    setTimeout(() => {
      this.classList.remove("dialog-polyfill-as-modal");
      this.classList.remove("closing");
      this.removeAttribute("open");
      this.setAttribute("aria-modal", "false");
      this.__backdrop?.remove();
      this.removeEventListener("blur", preventBlurListener, true);
      removeEventListener("keydown", escListener, true);
      if (returnValue != null) activeDialog!.returnValue = returnValue;
      activeDialog = null;
      this.__lastActiveElement?.focus();
      const event = new Event("close", { bubbles: false });
      this.dispatchEvent(event);
    }, 500); // Match the CSS transition duration ($duration-500)
  };

  Object.defineProperty(proto, "open", {
    get(): boolean {
      return this.hasAttribute("open");
    },
    set(value: boolean): void {
      value ? this.open() : this.close();
    },
  });

  document.addEventListener(
    "submit",
    (e: SubmitEvent) => {
      const target = e.target as HTMLFormElement;
      if (target.getAttribute("method") !== "dialog") return;
      e.preventDefault();
      activeDialog?.close((e.submitter as HTMLButtonElement)?.value);
    },
    true,
  );

  const css =
    "dialog{" +
    "display:block;" +
    "position:absolute;" +
    "left:0;" +
    "right:0;" +
    "width:fit-content;" +
    "height:fit-content;" +
    "margin:auto;" +
    "border-width:initial;" +
    "border-style:solid;" +
    "border-color:initial;" +
    "border-image:initial;" +
    "padding:1em;" +
    "background:white;" +
    "background:Canvas;" +
    "color:black;" +
    "color:CanvasText;" +
    "}" +
    "dialog:not([open]){" +
    "display:none;" +
    "}" +
    ".dialog-polyfill-as-modal{" +
    "position:fixed;" +
    "top:0;" +
    "bottom:0;" +
    "max-width:calc((100% - 6px) - 2em);" +
    "max-height:calc((100% - 6px) - 2em);" +
    "overflow:auto;" +
    "}" +
    "dialog + .backdrop{" +
    "position:fixed;" +
    "top:0;" +
    "right:0;" +
    "bottom:0;" +
    "left:0;" +
    "background:#0002;" +
    "}";
  document.head.insertAdjacentHTML("afterbegin", "<style>" + css + "</style>");
}
